<script setup lang="ts">
import { useAppMode } from '@/composables/useAppMode'

const { mode } = useAppMode()
withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'secondary-auto' | 'mobile-action'
    disabled?: boolean
    autoActionActive?: boolean
  }>(),
  {
    variant: 'primary'
  }
)
</script>
<template>
  <button
    :disabled="disabled"
    :class="{
      'game-button': true,
      [`game-button--${mode}`]: true,
      [`game-button--${variant}`]: true,
      'game-button--disabled': disabled,
      'game-button--autoaction-active': variant === 'secondary-auto' && autoActionActive
    }"
  >
    <span class="game-button__left-slot"><slot name="left" /></span>
    <span class="game-button__right-slot"><slot name="right" /></span>
    <slot />
    <div
      v-if="variant === 'secondary-auto'"
      class="game-button__autoaction"
      :class="{
        'game-button__autoaction--active': autoActionActive
      }"
    />
  </button>
</template>

<style lang="scss">
.game-button {
  position: relative;
  cursor: pointer;
  border: none;
  opacity: 0.9;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #fff;
  text-align: center;
  font-weight: 500;
  transition: all 0.3s ease-in-out;

  &:hover {
    opacity: 1;
  }

  &--desktop {
    width: 166px;
    font-size: 16px;
    height: min(65px, 12vh);
    border-radius: 14px;
  }

  &--mobile {
    width: 108px;
    font-size: 13px;
    height: 48px;
    border-radius: 8px;
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      opacity: 0.5 !important;
    }
  }

  &--secondary,
  &--secondary-auto {
    background: linear-gradient(180deg, rgba(42, 45, 49, 0.97) 0%, #0f1013 100%);
    border-radius: 14px;
    opacity: 0.9;
    box-shadow: 0px 2px 4.835286617279053px 0px rgba(0, 0, 0, 0.4);
  }

  &--primary {
    background: radial-gradient(214.52% 98.52% at 50% 0%, #025bde 0%, #051b3b 100%);
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.41);
  }

  &--mobile-action:not([disabled]) {
    background: radial-gradient(214.52% 98.52% at 50% 0%, #2a3249 0%, #071121 100%);
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.41);
    opacity: 1;
    height: 48px;
    border-radius: 8px;
  }

  &--autoaction-active {
    background:
      radial-gradient(
          60.53% 60.53% at 50% 100%,
          rgba(253, 208, 90, 0.14) 0%,
          rgba(223, 185, 82, 0) 100%
      ),
      linear-gradient(180deg, #25272f 0%, #0f0f16 100%);
    box-shadow: 0px 2px 4.835286617279053px 0px rgba(0, 0, 0, 0.4);
  }

  &__autoaction {
    position: absolute;
    width: 40px;
    height: 3px;
    border-radius: 6px;

    left: calc(50% - 20px);
    bottom: 3px;

    transition: all 0.3s ease-in-out;
    opacity: 0.1;
    background: #fff;
    box-shadow: inset 0px 2px 15px 0px rgba(0, 0, 0, 0.25);

    &--active {
      background: linear-gradient(45deg, rgba(215, 178, 80, 0.81) 0%, rgba(240, 201, 95, 0.73) 100%);
      box-shadow: none;
      opacity: 1;
    }
  }

  &__left-slot {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0.35;
    font-size: 9px;
    text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    padding: 4px 5px;
  }

  &__right-slot {
    position: absolute;
    top: 0;
    right: 0;
    padding: 5px;
    color: #ecb627;
    font-size: 9px;
    font-weight: 500;
    line-height: 120%;
    letter-spacing: 0.068px;
  }
}
</style>
